import { useState, useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
/**
 * useThirtyThirtyQueue Hook
 *
 * Core state management for 30/30 task queue app.
 * Manages queue state, timer logic, Supabase synchronization, and offline persistence.
 */
export function useThirtyThirtyQueue() {
    // Queue state
    const [queue, setQueue] = useState([]);
    const [activeItem, setActiveItem] = useState(null);
    const [completedItems, setCompletedItems] = useState([]);
    // Timer state
    const [isRunning, setIsRunning] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    // Loading and error state
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    // Refs for timer
    const timerIntervalRef = useRef(null);
    const lastTickTimeRef = useRef(0);
    /**
     * Denormalize queue item from database row
     */
    const denormalizeQueueItem = useCallback((row) => {
        let label = 'Unknown Item';
        let duration = 0;
        let color;
        let icon;
        let mantra;
        let startingRitual;
        let endingRitual;
        let audioUrl;
        let audioType;
        // Denormalize based on item type
        switch (row.item_type) {
            case 'step': {
                const step = row.steps;
                if (step) {
                    label = step.label || 'Unnamed Step';
                    duration = step.duration || 0;
                    color = step.color;
                    icon = step.icon;
                    mantra = step.mantra;
                    startingRitual = step.starting_ritual;
                    endingRitual = step.ending_ritual;
                    audioUrl = step.audio_url;
                    audioType = step.audio_type;
                }
                break;
            }
            case 'breath': {
                const breath = row.breaths;
                if (breath) {
                    label = breath.name || 'Unnamed Breath';
                    // Convert seconds to minutes for consistency
                    duration = Math.round((breath.time_estimation_seconds || 0) / 60);
                }
                break;
            }
            case 'length': {
                const length = row.lengths;
                if (length) {
                    label = length.name || 'Unnamed Length';
                    duration = length.duration || 0;
                }
                break;
            }
            case 'task_list': {
                const taskList = row.task_lists;
                if (taskList) {
                    label = taskList.name || 'Unnamed Task List';
                    // Task lists don't have inherent duration
                    duration = 0;
                }
                break;
            }
        }
        return {
            queueId: row.id,
            itemType: row.item_type,
            itemId: row.item_id,
            position: row.position,
            elapsedTimeSeconds: row.elapsed_time_seconds || 0,
            isActive: row.is_active || false,
            completedAt: row.completed_at || null,
            label,
            duration,
            color,
            icon,
            mantra,
            startingRitual,
            endingRitual,
            audioUrl,
            audioType,
            // Duration is in minutes, so convert elapsedTimeSeconds to minutes
            timeRemaining: Math.max(0, duration - Math.floor((row.elapsed_time_seconds || 0) / 60)),
            estimatedStartTime: new Date(),
            estimatedEndTime: new Date()
        };
    }, []);
    /**
     * Calculate timeline estimates (start/end times) for all queue items
     */
    const calculateTimelineEstimates = useCallback((items) => {
        const now = new Date();
        let cumulativeMinutes = 0;
        return items.map(item => {
            const startTime = new Date(now.getTime() + cumulativeMinutes * 60 * 1000);
            // Duration is in minutes, elapsedTimeSeconds needs to be converted
            const remainingMinutes = item.duration - Math.floor(item.elapsedTimeSeconds / 60);
            const endTime = new Date(startTime.getTime() + remainingMinutes * 60 * 1000);
            cumulativeMinutes += remainingMinutes;
            return {
                ...item,
                estimatedStartTime: startTime,
                estimatedEndTime: endTime
            };
        });
    }, []);
    /**
     * Fetch queue from Supabase with polymorphic joins
     */
    const fetchQueue = useCallback(async () => {
        try {
            console.log('[useThirtyThirtyQueue] fetchQueue starting...');
            setIsLoading(true);
            const { data: { user } } = await supabase.auth.getUser();
            if (!user) {
                console.warn('[useThirtyThirtyQueue] No user authenticated');
                setError('User not authenticated');
                setIsLoading(false);
                return;
            }
            console.log('[useThirtyThirtyQueue] Fetching queue for user:', user.id);
            // Fetch active and pending queue items (without joins first)
            const { data: queueData, error: queueError } = await supabase
                .from('queue_items')
                .select('*')
                .eq('user_id', user.id)
                .is('completed_at', null)
                .order('position', { ascending: true });
            if (queueError) {
                throw queueError;
            }
            // Fetch completed items
            const { data: completedData, error: completedError } = await supabase
                .from('queue_items')
                .select('*')
                .eq('user_id', user.id)
                .not('completed_at', 'is', null)
                .order('completed_at', { ascending: false });
            if (completedError) {
                throw completedError;
            }
            // Enrich each queue item with data from its source table
            const enrichQueueItems = async (items) => {
                return Promise.all(items.map(async (item) => {
                    let sourceData = null;
                    try {
                        switch (item.item_type) {
                            case 'step': {
                                const { data } = await supabase
                                    .from('steps')
                                    .select('id, label, duration, color, icon, mantra, starting_ritual, ending_ritual, audio_url, audio_type, elapsed_time')
                                    .eq('id', item.item_id)
                                    .single();
                                sourceData = data;
                                break;
                            }
                            case 'breath': {
                                const { data } = await supabase
                                    .from('breaths')
                                    .select('id, name, time_estimation_seconds, total_time_seconds')
                                    .eq('id', item.item_id)
                                    .single();
                                sourceData = data;
                                break;
                            }
                            case 'length': {
                                const { data, error: lengthError } = await supabase
                                    .from('lengths')
                                    .select('id, name, duration, position, type, frequency, recurrence_rule, recurrence_exception_dates')
                                    .eq('id', item.item_id)
                                    .single();
                                if (lengthError) {
                                    console.warn(`Failed to fetch length ${item.item_id}:`, lengthError);
                                }
                                else if (data) {
                                    console.log(`Fetched length ${item.item_id}:`, data);
                                    sourceData = data;
                                }
                                else {
                                    console.warn(`No data returned for length ${item.item_id}`);
                                }
                                break;
                            }
                            case 'task_list': {
                                // Legacy support: fetch from unified lengths table via source_table_id
                                const { data } = await supabase
                                    .from('lengths')
                                    .select('id, name, duration, type, frequency, recurrence_rule, recurrence_exception_dates')
                                    .eq('source_table_id', item.item_id)
                                    .single();
                                sourceData = data || { id: item.item_id, name: 'Unknown Task List' };
                                break;
                            }
                        }
                    }
                    catch (err) {
                        console.warn(`Failed to fetch ${item.item_type}:`, err);
                    }
                    // Return merged data
                    const result = { ...item };
                    if (sourceData) {
                        result[`${item.item_type}s`] = sourceData;
                    }
                    return result;
                }));
            };
            // Enrich queue data
            const enrichedQueue = await enrichQueueItems(queueData || []);
            const enrichedCompleted = await enrichQueueItems(completedData || []);
            // Denormalize and calculate timelines
            const pendingItems = calculateTimelineEstimates(enrichedQueue.map(denormalizeQueueItem));
            const completed = enrichedCompleted.map(denormalizeQueueItem);
            // Find active item, or use first item if none is marked active
            let active = pendingItems.find(item => item.isActive) || null;
            if (!active && pendingItems.length > 0) {
                // Auto-activate the first item if queue is not empty and no item is active
                active = pendingItems[0];
                // Update the database to mark this item as active
                const { error: autoActivateError } = await supabase
                    .from('queue_items')
                    .update({ is_active: true })
                    .eq('id', active.queueId);
                if (autoActivateError) {
                    console.warn('Failed to auto-activate first item:', autoActivateError);
                }
            }
            console.log('[useThirtyThirtyQueue] Queue fetched successfully:', {
                pendingCount: pendingItems.length,
                completedCount: completed.length,
                activeItem: active?.label || 'none'
            });
            setQueue(pendingItems);
            setActiveItem(active);
            setCompletedItems(completed);
            setError(null);
            // Try to restore timer state from localStorage
            const localState = loadFromLocalStorage();
            if (localState && localState.activeQueueId === active?.queueId) {
                setCurrentTime(localState.currentElapsedSeconds);
                setIsRunning(localState.isRunning);
            }
            else if (active) {
                setCurrentTime(active.elapsedTimeSeconds);
            }
        }
        catch (err) {
            console.error('[useThirtyThirtyQueue] Error fetching queue:', err);
            setError(err instanceof Error ? err.message : 'Failed to fetch queue');
            // Set empty queue to avoid blank screen
            setQueue([]);
            setActiveItem(null);
            setCompletedItems([]);
        }
        finally {
            setIsLoading(false);
            console.log('[useThirtyThirtyQueue] fetchQueue completed');
        }
    }, [denormalizeQueueItem, calculateTimelineEstimates]);
    /**
     * Save timer state to localStorage for offline support
     */
    const saveToLocalStorage = useCallback((state) => {
        try {
            localStorage.setItem('thirtythirty_state', JSON.stringify(state));
        }
        catch (err) {
            console.warn('Failed to save to localStorage:', err);
        }
    }, []);
    /**
     * Load timer state from localStorage
     */
    const loadFromLocalStorage = useCallback(() => {
        try {
            const stored = localStorage.getItem('thirtythirty_state');
            if (!stored)
                return null;
            const state = JSON.parse(stored);
            // If was running, calculate offline time
            if (state.isRunning && state.lastTickTimestamp) {
                const offlineSeconds = Math.floor((Date.now() - state.lastTickTimestamp) / 1000);
                state.currentElapsedSeconds += offlineSeconds;
            }
            return state;
        }
        catch (err) {
            console.warn('Failed to load from localStorage:', err);
            return null;
        }
    }, []);
    /**
     * Timer tick loop - runs every second
     */
    useEffect(() => {
        if (!isRunning || !activeItem) {
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
            }
            return;
        }
        timerIntervalRef.current = setInterval(() => {
            setCurrentTime(prev => {
                const newTime = prev + 1;
                // Auto-complete when duration reached
                if (newTime >= activeItem.duration) {
                    setIsRunning(false);
                    // completeActiveTask will be called separately
                    return activeItem.duration;
                }
                // Save to localStorage every tick
                saveToLocalStorage({
                    activeQueueId: activeItem.queueId,
                    currentElapsedSeconds: newTime,
                    isRunning: true,
                    lastTickTimestamp: Date.now()
                });
                // Update DB every 10 seconds (not every tick for performance)
                if (newTime % 10 === 0) {
                    supabase
                        .from('queue_items')
                        .update({ elapsed_time_seconds: newTime })
                        .eq('id', activeItem.queueId)
                        .then(() => {
                        // Update activeItem state
                        setActiveItem(prev => prev ? { ...prev, elapsedTimeSeconds: newTime } : null);
                    })
                        .catch(err => console.error('Error updating elapsed time:', err));
                }
                return newTime;
            });
            lastTickTimeRef.current = Date.now();
        }, 1000);
        return () => {
            if (timerIntervalRef.current) {
                clearInterval(timerIntervalRef.current);
                timerIntervalRef.current = null;
            }
        };
    }, [isRunning, activeItem, saveToLocalStorage]);
    /**
     * Add item to queue
     */
    const addToQueue = useCallback(async (itemType, itemId) => {
        try {
            console.log('[useThirtyThirtyQueue] addToQueue called with:', { itemType, itemId });
            const { data: { user } } = await supabase.auth.getUser();
            if (!user)
                throw new Error('Not authenticated');
            console.log('[useThirtyThirtyQueue] User authenticated:', user.id);
            // Get next position
            const nextPosition = queue.length > 0
                ? Math.max(...queue.map(item => item.position)) + 1
                : 1;
            console.log('[useThirtyThirtyQueue] Next position:', nextPosition, 'Current queue length:', queue.length);
            console.log('[useThirtyThirtyQueue] Inserting into queue_items...');
            const { data, error } = await supabase
                .from('queue_items')
                .insert({
                user_id: user.id,
                item_type: itemType,
                item_id: itemId,
                position: nextPosition,
                elapsed_time_seconds: 0,
                is_active: false
            })
                .select()
                .single();
            if (error) {
                console.error('[useThirtyThirtyQueue] Insert error:', error);
                throw error;
            }
            console.log('[useThirtyThirtyQueue] Successfully inserted:', data);
            // Refetch queue to get denormalized data
            console.log('[useThirtyThirtyQueue] Calling fetchQueue to refresh...');
            await fetchQueue();
            console.log('[useThirtyThirtyQueue] Queue fetched successfully');
        }
        catch (err) {
            console.error('[useThirtyThirtyQueue] Error adding to queue:', err);
            setError(err instanceof Error ? err.message : 'Failed to add to queue');
        }
    }, [queue, fetchQueue]);
    /**
     * Remove item from queue
     */
    const removeFromQueue = useCallback(async (queueId) => {
        try {
            const { error } = await supabase
                .from('queue_items')
                .delete()
                .eq('id', queueId);
            if (error)
                throw error;
            // Refetch queue
            await fetchQueue();
        }
        catch (err) {
            console.error('Error removing from queue:', err);
            setError(err instanceof Error ? err.message : 'Failed to remove from queue');
        }
    }, [fetchQueue]);
    /**
     * Reorder queue (drag-and-drop)
     */
    const reorderQueue = useCallback(async (queueId, newPosition) => {
        try {
            // Find the item being moved
            const movingItem = queue.find(item => item.queueId === queueId);
            if (!movingItem)
                return;
            const oldPosition = movingItem.position;
            // Update local queue optimistically
            const updatedQueue = queue.map(item => {
                if (item.queueId === queueId) {
                    return { ...item, position: newPosition };
                }
                // Shift other items
                if (oldPosition < newPosition) {
                    // Moving down
                    if (item.position > oldPosition && item.position <= newPosition) {
                        return { ...item, position: item.position - 1 };
                    }
                }
                else {
                    // Moving up
                    if (item.position >= newPosition && item.position < oldPosition) {
                        return { ...item, position: item.position + 1 };
                    }
                }
                return item;
            });
            // Sort by position
            updatedQueue.sort((a, b) => a.position - b.position);
            setQueue(updatedQueue);
            // Update in database
            const { error } = await supabase
                .from('queue_items')
                .update({ position: newPosition })
                .eq('id', queueId);
            if (error)
                throw error;
            // Recalculate timelines
            const withTimelines = calculateTimelineEstimates(updatedQueue);
            setQueue(withTimelines);
        }
        catch (err) {
            console.error('Error reordering queue:', err);
            setError(err instanceof Error ? err.message : 'Failed to reorder queue');
            // Refetch on error to restore correct state
            await fetchQueue();
        }
    }, [queue, calculateTimelineEstimates, fetchQueue]);
    /**
     * Switch active task
     */
    const switchActiveTask = useCallback(async (queueId) => {
        try {
            // Save current task's elapsed time
            if (activeItem) {
                await supabase
                    .from('queue_items')
                    .update({
                    elapsed_time_seconds: currentTime,
                    is_active: false
                })
                    .eq('id', activeItem.queueId);
            }
            // Set new task as active (without joins)
            const { data, error } = await supabase
                .from('queue_items')
                .update({ is_active: true })
                .eq('id', queueId)
                .select('*')
                .single();
            if (error)
                throw error;
            // Fetch source data and enrich
            let enrichedData = { ...data };
            const queueItem = data;
            try {
                switch (queueItem.item_type) {
                    case 'step': {
                        const { data: stepData } = await supabase
                            .from('steps')
                            .select('id, label, duration, color, icon, mantra, starting_ritual, ending_ritual, audio_url, audio_type, elapsed_time')
                            .eq('id', queueItem.item_id)
                            .single();
                        if (stepData)
                            enrichedData.steps = stepData;
                        break;
                    }
                    case 'breath': {
                        const { data: breathData } = await supabase
                            .from('breaths')
                            .select('id, name, time_estimation_seconds, total_time_seconds')
                            .eq('id', queueItem.item_id)
                            .single();
                        if (breathData)
                            enrichedData.breaths = breathData;
                        break;
                    }
                    case 'length': {
                        const { data: lengthData } = await supabase
                            .from('lengths')
                            .select('id, name, duration, position, type, frequency, recurrence_rule, recurrence_exception_dates')
                            .eq('id', queueItem.item_id)
                            .single();
                        if (lengthData)
                            enrichedData.lengths = lengthData;
                        break;
                    }
                    case 'task_list': {
                        // Legacy support: fetch from unified lengths table via source_table_id
                        const { data: taskListData } = await supabase
                            .from('lengths')
                            .select('id, name, duration, type, frequency, recurrence_rule, recurrence_exception_dates')
                            .eq('source_table_id', queueItem.item_id)
                            .single();
                        if (taskListData)
                            enrichedData.task_lists = taskListData;
                        break;
                    }
                }
            }
            catch (enrichErr) {
                console.warn('Failed to enrich task data:', enrichErr);
            }
            // Denormalize and set as active
            const newActiveItem = denormalizeQueueItem(enrichedData);
            setActiveItem(newActiveItem);
            setCurrentTime(newActiveItem.elapsedTimeSeconds);
            // Start timer
            setIsRunning(true);
            // Save to localStorage
            saveToLocalStorage({
                activeQueueId: queueId,
                currentElapsedSeconds: newActiveItem.elapsedTimeSeconds,
                isRunning: true,
                lastTickTimestamp: Date.now()
            });
            // Refetch to update queue active state
            await fetchQueue();
        }
        catch (err) {
            console.error('Error switching active task:', err);
            setError(err instanceof Error ? err.message : 'Failed to switch task');
        }
    }, [activeItem, currentTime, denormalizeQueueItem, saveToLocalStorage, fetchQueue]);
    /**
     * Complete active task
     */
    const completeActiveTask = useCallback(async () => {
        if (!activeItem)
            return;
        try {
            setIsRunning(false);
            // Update elapsed_time in source table based on item type
            if (activeItem.itemType === 'step') {
                await supabase
                    .from('steps')
                    .update({ elapsed_time: currentTime })
                    .eq('id', activeItem.itemId);
            }
            else if (activeItem.itemType === 'breath') {
                await supabase
                    .from('breaths')
                    .update({ total_time_seconds: currentTime })
                    .eq('id', activeItem.itemId);
            }
            // Mark queue item as completed
            const { error } = await supabase
                .from('queue_items')
                .update({
                completed_at: new Date().toISOString(),
                is_active: false,
                elapsed_time_seconds: currentTime
            })
                .eq('id', activeItem.queueId);
            if (error)
                throw error;
            // Clear active item and localStorage
            setActiveItem(null);
            setCurrentTime(0);
            saveToLocalStorage({
                activeQueueId: null,
                currentElapsedSeconds: 0,
                isRunning: false,
                lastTickTimestamp: Date.now()
            });
            // Refetch queue
            await fetchQueue();
            // Auto-activate next item if available
            const nextItem = queue.find(item => item.position === activeItem.position + 1);
            if (nextItem) {
                await switchActiveTask(nextItem.queueId);
            }
        }
        catch (err) {
            console.error('Error completing task:', err);
            setError(err instanceof Error ? err.message : 'Failed to complete task');
        }
    }, [activeItem, currentTime, queue, saveToLocalStorage, fetchQueue, switchActiveTask]);
    /**
     * Sync queue order back to source tables
     */
    const syncQueueOrderToSource = useCallback(async (queueItems) => {
        try {
            // Update position in each source table
            for (const item of queueItems) {
                if (item.itemType === 'step') {
                    await supabase
                        .from('steps')
                        .update({ position: item.position })
                        .eq('id', item.itemId);
                }
                else if (item.itemType === 'length') {
                    await supabase
                        .from('lengths')
                        .update({ position: item.position })
                        .eq('id', item.itemId);
                }
                // breaths and task_lists don't have position in their source tables
            }
            setError(null);
        }
        catch (err) {
            console.error('Error syncing queue order:', err);
            setError(err instanceof Error ? err.message : 'Failed to sync queue order');
        }
    }, []);
    /**
     * Timer control methods
     */
    const startTimer = useCallback(() => {
        if (activeItem) {
            setIsRunning(true);
        }
    }, [activeItem]);
    const pauseTimer = useCallback(() => {
        setIsRunning(false);
        if (activeItem) {
            saveToLocalStorage({
                activeQueueId: activeItem.queueId,
                currentElapsedSeconds: currentTime,
                isRunning: false,
                lastTickTimestamp: Date.now()
            });
        }
    }, [activeItem, currentTime, saveToLocalStorage]);
    const resetTimer = useCallback(() => {
        setCurrentTime(0);
        setIsRunning(false);
        if (activeItem) {
            saveToLocalStorage({
                activeQueueId: activeItem.queueId,
                currentElapsedSeconds: 0,
                isRunning: false,
                lastTickTimestamp: Date.now()
            });
        }
    }, [activeItem, saveToLocalStorage]);
    /**
     * Initial fetch on mount
     */
    useEffect(() => {
        console.log('[useThirtyThirtyQueue] Mounting, initial fetchQueue...');
        fetchQueue();
    }, [fetchQueue]);
    return {
        queue,
        activeItem,
        completedItems,
        isRunning,
        currentTime,
        addToQueue,
        removeFromQueue,
        reorderQueue,
        switchActiveTask,
        startTimer,
        pauseTimer,
        resetTimer,
        completeActiveTask,
        syncQueueOrderToSource,
        refreshQueue: fetchQueue,
        isLoading,
        error
    };
}
